import React, { useEffect, useState } from 'react';
import { UsersApi } from '../../../services/users';
import { LeaveService } from '../../../services/leave';
import { toast } from 'react-toastify';
// Removed custom Button import

interface ApproverStep {
  id: number;
  approvers: string[];
  stepName?: string;
}

interface HierarchyMap {
  [designation: string]: ApproverStep[];
}

const LeaveApprovalHierarchy: React.FC = () => {
  const { getAllJobRole } = UsersApi();
  const [designations, setDesignations] = useState<any[]>([]);
  const [selectedDesignation, setSelectedDesignation] = useState<string>('');
  const [approvalChain, setApprovalChain] = useState<ApproverStep[]>([]);
  const [hierarchy, setHierarchy] = useState<HierarchyMap>({});
  const { fetchAllHierarchies, fetchHierarchyByDesignation, saveHierarchy } = LeaveService();
  const [approverInput, setApproverInput] = useState<string[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [approverSearch, setApproverSearch] = useState('');

  useEffect(() => {
    async function fetchDesignationsAndHierarchies() {
      const res = await getAllJobRole();
      setDesignations(res.data.data || []);
      // Fetch all hierarchies from server
      const hierRes = await fetchAllHierarchies();
      if (hierRes?.data) {
        // Convert array to map: { designation: steps[] }
        const map: HierarchyMap = {};
        (Array.isArray(hierRes.data) ? hierRes.data : []).forEach((item: any) => {
          map[item.designation] = (item.steps || []).map((step: any, idx: number) => ({
            id: Date.now() + idx, // generate id for React
            approvers: step.approvers || [],
          }));
        });
        setHierarchy(map);
      }
    }
    fetchDesignationsAndHierarchies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchChain() {
      if (selectedDesignation) {
        const res = await fetchHierarchyByDesignation(selectedDesignation);
        // res.data is a single object
        const item = res?.data;
        const steps = item?.steps || [];
        setApprovalChain(
          steps.map((step: any, idx: number) => ({
            id: idx, // or step.stepNumber if you want
            approvers: step.approvers || [],
          }))
        );
      } else {
        setApprovalChain([]);
      }
    }
    fetchChain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDesignation]);

  const handleAddStep = () => {
    if (!approverInput.length) return;
    if (editIndex !== null) {
      const updated = [...approvalChain];
      updated[editIndex] = { id: Date.now(), approvers: approverInput };
      setApprovalChain(updated);
      setEditIndex(null);
    } else {
      setApprovalChain([...approvalChain, { id: Date.now(), approvers: approverInput }]);
    }
    setApproverInput([]);
  };

  const handleEditStep = (idx: number) => {
    setApproverInput(approvalChain[idx].approvers);
    setEditIndex(idx);
  };

  const handleRemoveStep = (idx: number) => {
    setApprovalChain(approvalChain.filter((_, i) => i !== idx));
    setEditIndex(null);
    setApproverInput([]);
  };

  const handleSaveHierarchy = async () => {
    if (!selectedDesignation) return;
    // Prepare steps for API
    const steps = approvalChain.map((step, idx) => ({
      stepNumber: idx + 1,
      approvers: step.approvers,
    }));
    await saveHierarchy(selectedDesignation, steps);
    // Refresh all hierarchies after save
    const hierRes = await fetchAllHierarchies();

    if (hierRes?.data) {
      const map: HierarchyMap = {};
      (Array.isArray(hierRes.data) ? hierRes.data : []).forEach((item: any) => {
        map[item.designation] = (item.steps || []).map((step: any, idx: number) => ({
          id: Date.now() + idx,
          approvers: step.approvers || [],
        }));
      });
      setHierarchy(map);
      toast.success("Added Successfully !");
      setApprovalChain([]);
      setApproverInput([]);
      setEditIndex(null);
      setSelectedDesignation('')
    }
  };

  const handleReset = () => {
    setApprovalChain([]);
    setApproverInput([]);
    setEditIndex(null);
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg max-w-5xl mx-auto mt-6">
      <h2 className="text-2xl font-bold mb-4 text-green-800 text-center">Leave Approval Hierarchy</h2>
      <div className="mb-6">
        <label className="block font-semibold mb-2 text-center">Select Designation</label>
        <select
          className="w-full border rounded px-3 py-2 mb-2"
          value={selectedDesignation}
          onChange={e => setSelectedDesignation(e.target.value)}
        >
          <option value="">-- Select Designation --</option>
          {designations.map((d: any) => (
            <option key={d.id} value={d.designation}>{d.designation}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label className="block font-semibold mb-2">Approval Step: Select Approver(s)</label>
        <input
          type="text"
          className="mb-2 w-full border rounded px-3 py-2"
          placeholder="Search designation..."
          value={approverSearch}
          onChange={e => setApproverSearch(e.target.value)}
        />
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex flex-wrap gap-4 max-h-32 overflow-y-auto border rounded px-3 py-2 bg-gray-50 shadow-sm">
            {designations
              .filter((d: any) => d.designation.toLowerCase().includes(approverSearch.toLowerCase()))
              .map((d: any) => (
                <label key={d.id} className="flex items-center gap-2 min-w-[160px] text-sm">
                  <input
                    type="checkbox"
                    value={d.designation}
                    checked={approverInput.includes(d.designation)}
                    onChange={e => {
                      if (e.target.checked) {
                        setApproverInput([...approverInput, d.designation]);
                      } else {
                        setApproverInput(approverInput.filter(a => a !== d.designation));
                      }
                    }}
                  />
                  <span>{d.designation}</span>
                </label>
              ))}
          </div>
          <div className="flex gap-2 mt-3 justify-end">
            <button type="button" onClick={handleAddStep} className="px-3 py-1 text-xs rounded bg-green-600 hover:bg-green-700 text-white">Add</button>
            <button type="button" onClick={handleReset} className="px-3 py-1 text-xs rounded bg-gray-400 hover:bg-gray-500 text-white">Reset</button>
          </div>
        </div>
      </div>
      <hr className="my-4 border-gray-200" />
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Approval Steps</h3>
        {approvalChain.length === 0 ? (
          <div className="text-gray-500 text-sm mb-2">No steps added yet.</div>
        ) : (
          <ol className="list-decimal pl-6 space-y-2">
            {approvalChain.map((step, idx) => (
              <li key={step.id} className="flex items-center gap-2 mb-1 text-sm">
                <span className="font-semibold mr-2">{`Step ${idx + 1}`}</span>
                <span className="flex-1">{step.approvers.join(', ')}</span>
                <button type="button" onClick={() => handleEditStep(idx)} className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white">Edit</button>
                <button type="button" onClick={() => handleRemoveStep(idx)} className="px-3 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white">Remove</button>
              </li>
            ))}
          </ol>
        )}
      </div>
      <div className="flex justify-end mb-8">
        <button type="button" onClick={handleSaveHierarchy} disabled={!selectedDesignation} className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white">Save</button>
      </div>
      <hr className="my-4 border-gray-200" />
      <div>
        <h3 className="text-lg font-semibold mb-2 text-center">Current Saved Hierarchy</h3>
        <table className="w-full border rounded text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border">Designation</th>
              <th className="py-2 px-4 border">Approval Chain</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(hierarchy).map(([designation, chain]) => (
              <tr key={designation}>
                <td className="py-2 px-4 border font-bold">{designation}</td>
                <td className="py-2 px-4 border">
                  {chain.map((step, idx) => (
                    <span key={step.id}><span className="font-semibold">{`Step ${idx + 1}`}</span>: {step.approvers.join(', ')}{idx < chain.length - 1 ? ' → ' : ''}</span>
                  ))}
                </td>
              </tr>
            ))}
            {Object.keys(hierarchy).length === 0 && (
              <tr>
                <td colSpan={2} className="text-center py-4 text-gray-500">No hierarchy defined yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveApprovalHierarchy; 